import { Alert, Box, Button, FormControl, FormControlLabel, FormHelperText, FormLabel, Link as MuiLink, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CustomSwitch } from '../atoms/CustomSwitch'
import { callAxios } from '../../hooks/useAxios'
import { useApp } from '../../hooks/useApp'
import { BrpMethod, CompanyInfo, ElectricityContractType, ReserveParty, TimeHHMM, UserSettings, VatSource, ZipCode } from '../../types/models'
import { PageSubtitle } from '../atoms/PageSubtitle'
import { lang } from '../../utils/lang'
import { setCaretPosition } from '../../utils/misc'
import { formatBusinessId, isValidBusinessId } from '../../utils/business-id'
import { Link } from 'react-router-dom'
import { FuseMeteringSettings, FuseProtectionForm, isValidFuseSettings } from './FuseProtectionForm'
import { isValidPowerFeeSettings, powerFeeFromConfig, PowerFeeForm, PowerFeeSettings } from './PowerFeeForm'
import { useDevices } from '../../hooks/useDevices'
import { DeviceControl } from '../../classes/DeviceControl'
import { OptimizerPortControl } from '../../classes/ports/OptimizerPortControl'
import { UncommittedConfig } from '../../types/ports'
import { Dropdown } from '../atoms/Dropdown'
import { Autocomplete } from '../atoms/Autocomplete'
import { Appliance, ApplianceData } from '../../classes/appliances/Appliance'
import { useAuth } from '../../hooks/useAuth'
import { reportClientError } from '../../utils/error-handler'
import { formatIban, isValidIban, normalizeIban } from '../../utils/iban'
import { isValidMeterNumber, normalizeMeterNumber } from '../../utils/meter-number'
import { DatahubGuideDialog } from '../DatahubGuideDialog'

interface DatahubStatus {
  enabled: boolean
  authUrl: string
  authorized: boolean
  info: {
    supplierName: string | null
    supplierCode: string | null
    brpCode: string
    brpName: string | null
    validFrom: string | null
    validTo: string | null
    fetchedAt: string
  } | null
}

export interface SettingsFormProps {
}

export function SettingsForm(props: SettingsFormProps): React.ReactElement {

  const { t } = useTranslation()
  const app = useApp()
  const settings = app.user?.settings || {}
  const [contractType, setContractType] = useState<ElectricityContractType|''>(settings.contractType || '')
  const [electricityPrice, setElectricityPrice] = useState(typeof settings.electricityPrice === 'number' ? `${parseFloat((Math.round((settings.electricityPrice * 100) * 100) / 100) + '')}` : '')
  const [marginPrice, setMarginPrice] = useState(typeof settings.marginPrice === 'number' ? `${parseFloat((Math.round((settings.marginPrice * 100) * 100) / 100) + '')}` : '')
  const [transferPrice, setTransferPrice] = useState(typeof settings.transferPrice === 'number' ? `${parseFloat((Math.round((settings.transferPrice * 100) * 100) / 100) + '')}` : '')
  const [cheapTransferPrice, setCheapTransferPrice] = useState(typeof settings.cheapTransferPrice === 'number' ? `${parseFloat((Math.round((settings.cheapTransferPrice * 100) * 100) / 100) + '')}` : '')
  const [hasCheapTransfer, setHasCheapTransfer] = useState(settings.hasCheapTransfer || false)
  const [cheapStartTime, setCheapStartTime] = useState(settings.cheapStartTime || '')
  const [cheapEndTime, setCheapEndTime] = useState(settings.cheapEndTime || '')
  const [meterNumber, setMeterNumber] = useState(settings.meterNumber || '')
  const [zipCode, setZipCode] = useState(settings.zipCode || '')
  const [reserveMarketsOn, setReserveMarketsOn] = useState(settings.hasReserveMarkets || false)
  // With an FCR battery the reserve features stay on: switching them off would only hide
  // the bank account field, not take the battery out of the reserve.
  const [hasFcrBattery, setHasFcrBattery] = useState(false)
  const hasReserveMarkets = reserveMarketsOn || hasFcrBattery
  const [hasAcceptReserveMarkets, setHasAcceptReserveMarkets] = useState(settings.hasAcceptReserveMarkets || false)
  const [contractName, setContractName] = useState(settings.contractName || '')
  const [contractAddress, setContractAddress] = useState(settings.contractAddress || '')
  const storedIban = settings.iban || ''
  const [iban, setIban] = useState(formatIban(storedIban))
  const [isVatRegistered, setIsVatRegistered] = useState(settings.isVatRegistered || false)
  const [reserveParty, setReserveParty] = useState<ReserveParty>(settings.reserveParty || (settings.isVatRegistered ? 'COMPANY' : 'PERSON'))
  const [businessId, setBusinessId] = useState(settings.reserveBusinessId || '')
  const [vatSource, setVatSource] = useState<VatSource>(settings.reserveVatSource || 'USER')
  const [vatCheckedAt, setVatCheckedAt] = useState(settings.reserveVatCheckedAt || '')
  const [company, setCompany] = useState<CompanyInfo | null>(null)
  const [lookupError, setLookupError] = useState('')
  const [checking, setChecking] = useState(false)
  const [retailerCode, setRetailerCode] = useState<string | null>(settings.retailerCode || null)
  const [retailers, setRetailers] = useState<{ code: string, name: string }[]>([])
  // Existing users who already picked a retailer stay on the manual path;
  // everyone else is steered to the recommended Datahub authorization.
  const [brpMethod, setBrpMethod] = useState<BrpMethod>(settings.brpMethod || (settings.retailerCode ? 'manual' : 'datahub'))
  const [datahubStatus, setDatahubStatus] = useState<DatahubStatus | null>(null)
  const [datahubGuideOpen, setDatahubGuideOpen] = useState(false)
  const { updateMe } = useAuth()

  // The register is the only source of VAT liability: there is no VAT choice in the UI,
  // only the party radios. The saved settings still matter, because the users migrated
  // from the old declared VAT switch keep their stored liability without a business ID.
  const savedParty: ReserveParty = settings.reserveParty || (settings.isVatRegistered ? 'COMPANY' : 'PERSON')
  const savedVatOn = savedParty === 'COMPANY' && !!settings.isVatRegistered
  // Editing the business ID after a check invalidates it, so the server would reject a
  // save that turns VAT on — tell the user before they hit the error.
  const vatNeedsCheck = reserveParty === 'COMPANY' && isVatRegistered && vatSource !== 'PRH' && !savedVatOn

  // A stored IBAN that predates validation may already be invalid: it shows the
  // error but only an *edited* invalid value blocks Save, so the user can still
  // change unrelated settings. Saving keeps the untouched stored value verbatim.
  const ibanInvalid = normalizeIban(iban) !== '' && !isValidIban(iban)
  const ibanEdited = normalizeIban(iban) !== normalizeIban(storedIban)
  const ibanBlocksSave = ibanInvalid && ibanEdited

  useEffect(() => {
    callAxios<{ code: string, name: string }[]>({ url: '/retailers' }).then(res => {
      if (res.success && Array.isArray(res.data)) {
        setRetailers(res.data)
      }
    })
    callAxios<DatahubStatus>({ url: '/datahub/status' }).then(res => {
      if (res.success && res.data) {
        setDatahubStatus(res.data)
      }
    })
    callAxios<ApplianceData[]>({ url: '/appliances' }).then(res => {
      if (res.success && Array.isArray(res.data)) {
        setHasFcrBattery(res.data.some(a => a.type === 'FCR_BATTERY'))
      }
    })
  }, [])

  const [fuseSettings, setFuseSettings] = useState<FuseMeteringSettings>({
    enabled: false,
    protectFuses: true,
    fuseSize: 25,
  })
  // Tehomaksu (power fee): lives in the optimizer port conf like the fuse settings.
  const [powerFee, setPowerFee] = useState<PowerFeeSettings>(powerFeeFromConfig({}, false))
  const { control } = useDevices()
  // The fuse size field is shown (and fixable) only on installer level; elsewhere a stored
  // out-of-range value is left to the server, which refuses it with a clear message.
  const fuseBlocksSave = app.installerLevel && fuseSettings.enabled && !isValidFuseSettings(fuseSettings)
  const powerFeeBlocksSave = powerFee.enabled && !isValidPowerFeeSettings(powerFee)

  useEffect(() => {
    const optimizers: OptimizerPortControl[] = control.ports({ portType: 'OPTIMIZER' })
    if (optimizers.length) {
      setFuseSettings(optimizers[0].getFuseSettings())
      // Every optimizer gets the same settings on save; read them from the multiopt
      // leader, which is the one that applies the tariff (followers ignore it).
      const leader = optimizers.find(o => o.getConfig('multiopt_role') === 'MULTIOPT_LEADER') || optimizers[0]
      setPowerFee(leader.getPowerFeeSettings())
    }
  }, [control])

  const onSaveOptimizerSettings = async () => {
    const items: UncommittedConfig[] = []
    control.ports({ portType: 'OPTIMIZER' }).forEach(ctrl => {
      const optCtrl = ctrl as OptimizerPortControl
      optCtrl.saveSettings(fuseSettings, powerFee)
      items.push(DeviceControl.buildPortFlashItem(optCtrl))
    })
    try {
      const multiOptItems = await Appliance.configureMultiOpt(control)
      items.push(...multiOptItems)
    } catch (e) {
      console.error('configureMultiOpt failed, flashing collected items anyway:', e)
    }
    await control.flashItems(items)
    control.clearPendingConfig()
  }

  const onSaveUserSettings = async () => {

    const settings: UserSettings = {
      contractType,
      electricityPrice: electricityPrice === '' ? null : parseFloat((Math.round(+electricityPrice * 100) / 100) + '') / 100,
      marginPrice: marginPrice === '' ? null : parseFloat((Math.round(+marginPrice * 100) / 100) + '') / 100,
      transferPrice: transferPrice === '' ? null : parseFloat((Math.round(+transferPrice * 100) / 100) + '') / 100,
      cheapTransferPrice: cheapTransferPrice === '' ? null : parseFloat((Math.round(+cheapTransferPrice * 100) / 100) + '') / 100,
      cheapStartTime: cheapStartTime as TimeHHMM,
      cheapEndTime: cheapEndTime as TimeHHMM,
      hasCheapTransfer,
      meterNumber: normalizeMeterNumber(meterNumber),
      zipCode: zipCode as ZipCode,
      hasReserveMarkets: !!hasReserveMarkets,
      hasAcceptReserveMarkets: !!hasAcceptReserveMarkets,
      contractName: contractName || '',
      contractAddress: contractAddress || '',
      iban: isValidIban(iban) ? normalizeIban(iban) : (normalizeIban(iban) === '' ? '' : storedIban),
      retailerCode: retailerCode || null,
      brpMethod,
      // A private person carries no company data: the VAT liability and the business ID
      // belong to the company side even when the hidden fields still hold state.
      ...(reserveParty === 'COMPANY'
        ? {
            reserveParty: 'COMPANY' as ReserveParty,
            reserveBusinessId: businessId,
            isVatRegistered: !!isVatRegistered,
            reserveVatSource: vatSource,
            reserveVatCheckedAt: vatCheckedAt
          }
        : {
            reserveParty: 'PERSON' as ReserveParty,
            reserveBusinessId: '',
            isVatRegistered: false,
            reserveVatSource: 'USER' as VatSource,
            reserveVatCheckedAt: ''
          })
    }

    callAxios({
      url: '/auth/me',
      method: 'PATCH',
    }, { settings }).then(async res => {
      if (res.success) {
        await updateMe()
        app.addInfo(t('Settings saved successfully.'))
      } else {
        app.addError(res)
      }
    })
  }

  // An unexpected crash mid-save used to reject the click handler's promise
  // silently, leaving the user staring at an unchanged value with no error.
  const onSaveFailed = (e: unknown) => {
    console.error('Saving settings failed:', e)
    reportClientError(`Saving settings failed: ${e}`, e instanceof Error ? e.stack || '' : '')
    app.addError(t('Saving failed unexpectedly. Please reload the page and try again.'))
  }

  // A private person is never liable for VAT. Selecting the company keeps the VAT
  // liability out of the user's hands: it comes from the register check of this session,
  // or from the saved settings when the saved company is unchanged (the users migrated
  // from the old declared VAT switch have a stored liability but no business ID to
  // re-check), and otherwise it is off until the register says so.
  const onSelectReserveParty = (party: ReserveParty) => {
    setReserveParty(party)
    setLookupError('')
    if (party === 'PERSON') {
      setIsVatRegistered(false)
    } else if (company) {
      setIsVatRegistered(company.vatLiable)
      setVatSource('PRH')
    } else if (savedParty === 'COMPANY' && businessId === (settings.reserveBusinessId || '')) {
      setIsVatRegistered(settings.isVatRegistered || false)
      setVatSource(settings.reserveVatSource || 'USER')
      setVatCheckedAt(settings.reserveVatCheckedAt || '')
    } else {
      setIsVatRegistered(false)
      setVatSource('USER')
      setVatCheckedAt('')
    }
  }

  // Editing the business ID invalidates the lookup but keeps the chosen role: the VAT
  // liability becomes the user's own declaration until the new ID is checked.
  const onChangeBusinessId = (value: string) => {
    setBusinessId(formatBusinessId(value))
    setCompany(null)
    setLookupError('')
    setVatSource('USER')
    setVatCheckedAt('')
  }

  const onCheckBusinessId = async () => {
    setChecking(true)
    setLookupError('')
    const res = await callAxios<CompanyInfo>({ url: `/business/${businessId}` })
    setChecking(false)

    if (!res.success || !res.data) {
      setCompany(null)
      setLookupError(t('Company was not found. Check the business ID.'))
      return
    }

    const found = res.data
    setCompany(found)
    setIsVatRegistered(found.vatLiable)
    setVatSource('PRH')
    setVatCheckedAt(new Date().toISOString())
    if (!contractName) {
      setContractName(found.name || '')
    }
    if (!contractAddress) {
      setContractAddress(found.address || '')
    }
  }

  const handleRoundGridCost = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setTransferPrice('')
      return
    }
    setTransferPrice(parseFloat((Math.round(+e.target.value * 100) / 100) + '').toString())
  }

  const handleRoundSpotPrice = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setMarginPrice('')
      return
    }
    setMarginPrice(parseFloat((Math.round(+e.target.value * 100) / 100) + '').toString())
  }

  const handleRoundFixedPrice = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setElectricityPrice('')
      return
    }
    setElectricityPrice(parseFloat((Math.round(+e.target.value * 100) / 100) + '').toString())
  }

  const handleRoundCheapTransferPrice = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setCheapTransferPrice('')
      return
    }
    setCheapTransferPrice(parseFloat((Math.round(+e.target.value * 100) / 100) + '').toString())
  }

  return (
    <Box id="electricity-contract" component="form">
      <PageSubtitle sx={{ mt: 4 }} title={t('Electricity contract')}/>
      <FormControl data-cy="box-Electricity contract" component="div">
        <Dropdown error={contractType ? false : t('Plase select the type of the contract.')} items={{ '': '', MARKET: t('Contract-MARKET'), MARKET_HOURLY: t('Contract-MARKET_HOURLY'), MARKET_CONSUMPTION_IMPACT: t('Contract-MARKET_CONSUMPTION_IMPACT'), FIXED: t('Contract-FIXED') }} selected={contractType} onSelect={(e) => setContractType(e as ElectricityContractType | '')}/>
      </FormControl>
      {contractType === 'MARKET_HOURLY' &&
        <Alert severity="warning" sx={{ mt: 1 }} data-cy="alert-Hourly Contract">
          {t('Are you sure? Most spot price customers are billed in 15-minute periods. Choose this option only if your grid company has confirmed that you have an old-fashioned meter that only measures hourly.')}
        </Alert>}<br/>

      <FormControl component="div">
        {
          contractType === 'FIXED' && <>
            <TextField
              data-cy='text-Fixed price'
              margin="normal"
              name="electricityPrice"
              label={t('Fixed price ({unit}/kWh)').replace('{unit}', lang.moneyLongUnit(app.user?.currency || 'EUR'))}
              type="number"
              value={electricityPrice}
              onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
              onChange={(e) => setElectricityPrice(e.target.value)}
              onBlur={handleRoundFixedPrice}
              id="electricity-price"
              error={!electricityPrice}
              autoComplete="new-password"
              inputProps={{ lang: 'fi' }} // fix decimal separator for Firefox
            />
            {!electricityPrice && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
          </>
        }
        {
          (contractType === 'MARKET' || contractType === 'MARKET_HOURLY') && <>
            <TextField
              data-cy='text-Market price'
              margin="normal"
              name="marginPrice"
              value={marginPrice}
              onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
              onChange={(e) => setMarginPrice(e.target.value)}
              onBlur={handleRoundSpotPrice}
              label={t('Spot price marginal ({unit}/kWh)').replace('{unit}', lang.moneyLongUnit(app.user?.currency || 'EUR'))}
              type="number"
              id="margin-price"
              autoComplete="new-password"
              error={!marginPrice}
              inputProps={{ lang: 'fi' }} // fix decimal separator for Firefox
            />
            {!marginPrice && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
          </>
        }
      </FormControl><br/>

      <FormControl component="div">
        <TextField
          data-cy='text-Grid operator cost'
          margin="normal"
          name="transferPrice"
          value={transferPrice}
          onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
          onChange={(e) => setTransferPrice(e.target.value)}
          onBlur={handleRoundGridCost}
          label={t('Grid operator cost ({unit}/kWh)').replace('{unit}', lang.moneyLongUnit(app.user?.currency || 'EUR'))}
          type="number"
          id="transfer-price"
          autoComplete="new-password"
          error={!transferPrice}
          inputProps={{ lang: 'fi' }} // fix decimal separator for Firefox
        />
        {!transferPrice && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
        {!!transferPrice && parseFloat(transferPrice) < 5 && (
          <FormHelperText sx={{ color: 'warning.main' }}>
            <Trans>The grid fee seems too low — are you sure? All consumers and most companies belong to electricity tax class 1 (2.92 snt/kWh). Please report the grid fee including taxes.</Trans>
          </FormHelperText>
        )}
      </FormControl><br/>

      <PowerFeeForm settings={powerFee} onChange={s => setPowerFee(s)}/>

      <FormControl component="div">
        <Box>
          <CustomSwitch checked={hasCheapTransfer} onClick={() => setHasCheapTransfer(!hasCheapTransfer)} data-cy='switch-Cheap Hours' sx={{ mr: '1rem' }}/>
          <Trans>There are cheap hours with lower fee</Trans>
        </Box>
      </FormControl>

      {
        hasCheapTransfer && <>
          <FormControl sx={{ display: 'block' }} component="div">
            <TextField
              data-cy="text-Reduced grid fee"
              margin="normal"
              name="cheapTransferPrice"
              value={cheapTransferPrice}
              onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
              onChange={(e) => setCheapTransferPrice(e.target.value)}
              onBlur={handleRoundCheapTransferPrice}
              label={t('Reduced grid fee (e.g. night tariff)')}
              type="number"
              id="cheap-price"
              autoComplete="new-password"
              error={!cheapTransferPrice}
              inputProps={{ lang: 'fi' }} // fix decimal separator for Firefox
            />
            {!cheapTransferPrice && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
            {!!cheapTransferPrice && parseFloat(cheapTransferPrice) < 4 && (
              <FormHelperText sx={{ color: 'warning.main' }}>
                <Trans>The grid fee seems too low — are you sure? All consumers and most companies belong to electricity tax class 1 (2.92 snt/kWh). Please report the grid fee including taxes.</Trans>
              </FormHelperText>
            )}
          </FormControl>

          <Box>
            <TextField
              data-cy="text-Cheap start time"
              margin="normal"
              name="cheapStartTime"
              value={cheapStartTime}
              onChange={(e) => setCheapStartTime(e.target.value)}
              label={t('From')}
              type="time"
              id="cheap-start-time"
              autoComplete="new-password"
            />
            <TextField
              data-cy="text-Cheap end time"
              sx={{ ml: 2 }}
              margin="normal"
              name="cheapEndTime"
              value={cheapEndTime}
              onChange={(e) => setCheapEndTime(e.target.value)}
              label={t('To')}
              type="time"
              id="cheap-end-time"
              autoComplete="new-password"
            />
          </Box>
        </>
      }

      <Typography variant="h6" color="primary.main">
        <Trans>Please include taxes to all prices.</Trans>
      </Typography>

      <PageSubtitle sx={{ mt: 5 }} title={t('Location Details')}/>

      <Typography>
        <Trans>Location and meter number information is used for optimization e.g. fetching weather forecast and grid status.</Trans>
      </Typography>

      <FormControl component="div">
        <TextField
          data-cy='text-Meter Number'
          margin="normal"
          name="meterNumber"
          value={meterNumber}
          onChange={(e) => setMeterNumber(e.target.value)}
          onBlur={() => setMeterNumber(normalizeMeterNumber(meterNumber))}
          label={t('Meter Number')}
          id="meter-number"
          autoComplete="new-password"
          error={hasReserveMarkets && !meterNumber}
        />
        {!meterNumber && hasReserveMarkets && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
        {!!meterNumber && hasReserveMarkets && !isValidMeterNumber(meterNumber) &&
          <FormHelperText error data-cy='warning-Meter Number'>
            {t('The metering point number does not look valid (18 digits starting with 64).')}
          </FormHelperText>}
      </FormControl><br/>

      <FormControl component="div">
        <TextField
          data-cy='text-Zip Code'
          margin="normal"
          name="zipCode"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          label={t('Zip Code')}
          id="zip-code"
          autoComplete="new-password"
          error={!zipCode}
        />
        {!zipCode && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
      </FormControl><br/>

      {
        app.installerLevel && <Box>
          <PageSubtitle sx={{ mt: 7 }} title={t('Fuse protection')}/>
          <Link to="/appliances">
            <Trans>What or which energy meters measure main feed is configured from the energy meter settings (Appliances).</Trans>
          </Link>
          <Box sx={{ mt: 1 }}>
            <FuseProtectionForm settings={fuseSettings} onChange={s => setFuseSettings(s)}/>
          </Box>

        </Box>
      }

      <PageSubtitle sx={{ mt: 5 }} title={t('Reserve markets')}/>
      <Box>
          <CustomSwitch checked={hasReserveMarkets} disabled={hasFcrBattery} onClick={() => setReserveMarketsOn(!reserveMarketsOn)} data-cy='switch-Reserve Markets' sx={{ mr: '1rem' }}/>
          <Trans>Enable reserve market features</Trans>
          {hasFcrBattery &&
            <FormHelperText data-cy='hint-Reserve Markets locked'>
              <Trans>Reserve markets locked help</Trans>
            </FormHelperText>
          }
          {
            hasReserveMarkets && <Box>
              <FormControl component="fieldset" sx={{ mt: 2, display: 'block' }}>
                <FormLabel component="legend"><Trans>Contract party</Trans></FormLabel>
                <RadioGroup
                  value={reserveParty}
                  onChange={(e) => onSelectReserveParty(e.target.value as ReserveParty)}
                >
                  <FormControlLabel
                    value="PERSON"
                    control={<Radio data-cy='radio-Private person'/>}
                    label={t('Private person')}
                  />
                  <FormControlLabel
                    value="COMPANY"
                    control={<Radio data-cy='radio-Company'/>}
                    label={t('Company')}
                  />
                </RadioGroup>
                <FormHelperText>
                  <Trans>Affects the value added tax of your reserve market settlement.</Trans>
                </FormHelperText>
                {reserveParty === 'COMPANY' &&
                  <FormHelperText data-cy='text-VAT from register'>
                    <Trans>The VAT liability of a company is read from the YTJ register when you check the business ID.</Trans>
                  </FormHelperText>}
                {vatNeedsCheck &&
                  <FormHelperText error data-cy='text-VAT needs check'>
                    <Trans>Check the business ID again to confirm the VAT liability before saving.</Trans>
                  </FormHelperText>}
              </FormControl>

              {
                reserveParty === 'COMPANY' && <Box sx={{ mt: 1 }}>
                  <FormControl component="div" sx={{ mr: 2 }}>
                    <TextField
                      data-cy='text-Business ID'
                      margin="normal"
                      name="businessId"
                      value={businessId}
                      onChange={(e) => onChangeBusinessId(e.target.value)}
                      label={t('Business ID')}
                      placeholder="1234567-8"
                      id="business-id"
                      autoComplete="new-password"
                      error={!!businessId && !isValidBusinessId(businessId)}
                    />
                  </FormControl>
                  <Button
                    data-cy='button-Check Business ID'
                    variant="outlined"
                    sx={{ mt: 3 }}
                    disabled={checking || !isValidBusinessId(businessId)}
                    onClick={onCheckBusinessId}
                  >
                    {checking ? <Trans>Checking...</Trans> : <Trans>Check</Trans>}
                  </Button>
                  {!businessId && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
                  {!!businessId && !isValidBusinessId(businessId) &&
                    <FormHelperText error><Trans>The check digit of the business ID does not match.</Trans></FormHelperText>}
                  {!!lookupError && <FormHelperText error>{lookupError}</FormHelperText>}
                  {!company &&
                    <FormHelperText data-cy='text-VAT status'>
                      {isVatRegistered ? t('VAT is added to the settlement.') : t('Settlement without VAT.')}
                    </FormHelperText>}
                  {
                    company && <Box
                      data-cy='box-Company Details'
                      sx={{ mt: 1, mb: 1, pl: 1.5, borderLeft: '3px solid', borderColor: company.active ? 'primary.main' : 'error.main' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{company.name}</Typography>
                      {!company.active &&
                        <Typography variant="body2" color="error"><Trans>The business ID is no longer valid.</Trans></Typography>}
                      {company.situations.map(situation =>
                        <Typography key={situation} variant="body2" color="error">{situation}</Typography>)}
                      <Typography variant="body2">
                        {company.vatLiable
                          ? `${company.vatDescription}${company.vatRegisteredAt ? ` (${company.vatRegisteredAt})` : ''}`
                          : t('Not registered for VAT.')}
                      </Typography>
                      <Typography variant="body2">
                        {isVatRegistered ? t('VAT is added to the settlement.') : t('Settlement without VAT.')}
                      </Typography>
                    </Box>
                  }
                </Box>
              }

              <FormControl component="div">
                <TextField
                  data-cy='text-Name'
                  margin="normal"
                  name="contractName"
                  value={contractName}
                  onChange={(e) => setContractName(e.target.value)}
                  label={reserveParty === 'COMPANY' ? t('Company name in contract') : t('Name in Contract')}
                  id="contract-name"
                  autoComplete="new-password"
                />
              </FormControl><br/>

              <FormControl component="div">
                <TextField
                  data-cy='text-Address'
                  margin="normal"
                  name="contractAddress"
                  value={contractAddress}
                  onChange={(e) => setContractAddress(e.target.value)}
                  label={t('Address')}
                  id="contract-address"
                  autoComplete="new-password"
                />
              </FormControl><br/>

              <FormControl component="div">
                <TextField
                  data-cy='text-IBAN'
                  margin="normal"
                  name="iban"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  onBlur={(e) => setIban(formatIban(e.target.value))}
                  label={t('IBAN')}
                  id="iban"
                  autoComplete="new-password"
                  error={ibanInvalid}
                />
                {ibanInvalid && <FormHelperText error data-cy='error-IBAN'>{t('Check the account number (IBAN)')}</FormHelperText>}
              </FormControl><br/>

              <FormControl component="fieldset" sx={{ mt: 3, display: 'block' }}>
                <FormLabel component="legend">{t('Balance responsible party notification')}</FormLabel>
                <Typography variant="body2" sx={{ mt: 1, maxWidth: '40rem' }}>
                  <Trans>According to the reserve terms confirmed by the Energy Authority, we must notify the balance responsible party of your electricity retailer about reserve use.</Trans>
                </Typography>
                <RadioGroup value={brpMethod} onChange={(e) => setBrpMethod(e.target.value as BrpMethod)}>
                  <FormControlLabel
                    value="datahub"
                    control={<Radio data-cy='radio-Datahub authorization'/>}
                    label={t('I authorize the data transfer in Fingrid Datahub (recommended)')}
                  />
                  {brpMethod === 'datahub' && <Box sx={{ pl: 4, mb: 1, maxWidth: '40rem' }}>
                    <Typography variant="body2">
                      <Trans>The information updates automatically, also when you switch retailers. The authorization gives no access to your consumption data.</Trans>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 1 }}>
                      <Button
                        data-cy='button-Datahub authorization'
                        variant="outlined"
                        size="small"
                        href={datahubStatus?.authUrl || 'https://valtuuta.datahub.fi'}
                        target="_blank"
                        rel="noopener"
                      >
                        {t('Create the authorization in Datahub')}
                      </Button>
                      <MuiLink
                        component="button"
                        type="button"
                        data-cy='link-Datahub guide'
                        onClick={(e) => { e.preventDefault(); setDatahubGuideOpen(true) }}
                      >
                        {t('Instructions')}
                      </MuiLink>
                      <DatahubGuideDialog
                        open={datahubGuideOpen}
                        authUrl={datahubStatus?.authUrl || 'https://valtuuta.datahub.fi'}
                        onClose={() => setDatahubGuideOpen(false)}
                      />
                    </Box>
                    {datahubStatus?.info
                      ? <FormHelperText sx={{ color: 'success.main' }} data-cy='text-Datahub active'>
                          {t('Authorization active. Retailer according to Datahub:')}{' '}
                          {datahubStatus.info.supplierName || datahubStatus.info.supplierCode}
                          {datahubStatus.info.brpName ? ` (${t('balance responsible party')}: ${datahubStatus.info.brpName})` : ''}
                        </FormHelperText>
                      : datahubStatus?.authorized
                        ? <FormHelperText data-cy='text-Datahub waiting data'>
                            {t('Authorization registered — waiting for data from Datahub.')}
                          </FormHelperText>
                        : <FormHelperText data-cy='text-Datahub waiting auth'>
                            {t('Waiting for your authorization in Datahub. Make sure the metering point number above is correct — the authorization is matched by it.')}
                          </FormHelperText>}
                  </Box>}
                  <FormControlLabel
                    value="manual"
                    control={<Radio data-cy='radio-Manual retailer'/>}
                    label={t('I select my electricity retailer myself')}
                  />
                </RadioGroup>
                {brpMethod === 'manual' && <Box sx={{ pl: 4 }}>
                  <FormControl component="div" sx={{ minWidth: '20rem' }}>
                    <Autocomplete
                      label={t('Electricity retailer')}
                      options={retailers.map(r => r.name)}
                      value={retailers.find(r => r.code === retailerCode)?.name || null}
                      error={!retailerCode}
                      onChange={(_e, name) => {
                        setRetailerCode(retailers.find(r => r.name === name)?.code || null)
                      }}
                    />
                    {!retailerCode && <FormHelperText error>{t('This value is required.')}</FormHelperText>}
                    {!!datahubStatus?.info && !!retailerCode && datahubStatus.info.supplierCode !== retailerCode &&
                      <FormHelperText error data-cy='warning-Datahub mismatch'>
                        {t('According to Datahub your retailer is')}{' '}
                        {datahubStatus.info.supplierName || datahubStatus.info.supplierCode}.
                      </FormHelperText>}
                  </FormControl>
                </Box>}
              </FormControl><br/>

              <CustomSwitch checked={hasAcceptReserveMarkets} onClick={() => setHasAcceptReserveMarkets(!hasAcceptReserveMarkets)} data-cy='switch-Reserve Markets' sx={{ mr: '1rem' }}/>
              <Trans>I am contract participant and I do accept & agree</Trans> <a href="https://www.enion.fi/reservimarkkinaehdot" target="new"><Trans>terms and conditions for reserve market by Robustco Oy.</Trans></a>
            </Box>
          }
      </Box>

      <Button
        data-cy='button-Save Settings'
        variant="contained"
        fullWidth
        disabled={ibanBlocksSave || fuseBlocksSave || powerFeeBlocksSave}
        sx={{ mt: 3 }}
        onClick={() => onSaveUserSettings()
          .then(() => onSaveOptimizerSettings())
          .catch(onSaveFailed)
        }
      >
      <Trans>Save Settings</Trans>
    </Button>
  </Box>
  )
}
